import { usePostHog } from '@posthog/react';
import Tooltip from 'components/@extended/Tooltip';
import PermissionButton from 'components/buttons/PermissionButton.tsx';
import useAuth from 'hooks/useAuth.ts';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useImpersonateUserMutation } from 'store/api/user.ts';
import { NamedEntityMinDto } from 'types/other';

type Props = {
    employeeId?: string | number;
    workEmailAddress?: string | null;
    cognitoId?: string | null;
    isActive?: boolean;
    canBeImpersonatedBy?: NamedEntityMinDto[] | null;
    className?: string;
};

const ImpersonateButton = ({
    employeeId,
    workEmailAddress,
    cognitoId,
    isActive,
    canBeImpersonatedBy,
    className,
}: Props) => {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const [impersonateUser] = useImpersonateUserMutation();
    const { user, impersonateUser: setImpersonatedUser } = useAuth();
    const isImpersonationGrantedToCurrentUser =
        canBeImpersonatedBy?.some((employee) => employee.id === Number(user?.id)) ?? false;

    const onSubmit = async (id: number) => {
        await impersonateUser(id)
            .unwrap()
            .then((impersonatedUserData) => {
                posthog.capture('user_impersonation_started');
                setImpersonatedUser(impersonatedUserData);
                navigate(`/home`);
            })
            .catch(console.error);
    };

    const getTooltipTextId = () => {
        if (!employeeId) {
            return 'impersonate-no-employee-id';
        }
        if (!workEmailAddress) {
            return 'impersonate-no-work-email';
        }
        if (!cognitoId) {
            return 'impersonate-no-cognito-id';
        }
        if (!isActive) {
            return 'impersonate-no-active-employee';
        }
        return undefined;
    };

    return (
        // The tooltip hangs on the wrapper: the disabled button itself takes no pointer events.
        <Tooltip text={getTooltipTextId()}>
            <div>
                <PermissionButton
                    key="impersonate"
                    permissions={[`ADMIN.ACCOUNT_TAKEOVER`]}
                    hasAuthority={isImpersonationGrantedToCurrentUser}
                    variant="outlined"
                    size="small"
                    className={className}
                    onClick={() => {
                        void onSubmit(Number(employeeId));
                    }}
                    disabled={!employeeId || !workEmailAddress || !cognitoId || !isActive}
                >
                    <FormattedMessage id="impersonate" />
                </PermissionButton>
            </div>
        </Tooltip>
    );
};

export default ImpersonateButton;
